import { Box, Card, Chip, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography } from '@mui/material';
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../../shared/api';
import type { AuditLogEntry } from '../../../shared/types';
import PageHeader from '../../../shared/PageHeader';
import { formatDateTime } from '../../../shared/utils';

const ACTION_COLORS: Record<string, string> = {
  CREATE_NOTICE: 'primary', UPDATE_NOTICE: 'info', DELETE_NOTICE: 'error',
  CREATE_STUDENT: 'success', UPDATE_STUDENT: 'info', DELETE_STUDENT: 'error',
  BULK_CREATE_STUDENTS: 'success',
  CREATE_TEACHER: 'success', UPDATE_TEACHER: 'info', DELETE_TEACHER: 'error',
  PUBLISH_RESULT: 'secondary', SUBMIT_MARKS: 'info', UPDATE_MARKS_BULK: 'info',
  RECORD_PAYMENT: 'success',
  LOGIN: 'default', LOGOUT: 'default', CHANGE_PASSWORD: 'warning',
  MARK_ATTENDANCE: 'info',
};

export default function AuditLogPage() {
  const [filter, setFilter] = useState('');
  const { data } = useQuery({ queryKey: ['audit-log'], queryFn: async () => (await api.get<AuditLogEntry[]>('/audit-log')).data });

  const filtered = useMemo(() => {
    const list = data ?? [];
    if (!filter) return list;
    const q = filter.toLowerCase();
    return list.filter((e) =>
      e.action.toLowerCase().includes(q) ||
      e.entityType.toLowerCase().includes(q) ||
      e.actorName.toLowerCase().includes(q) ||
      JSON.stringify(e.metadata).toLowerCase().includes(q)
    );
  }, [data, filter]);

  return (
    <Box>
      <PageHeader title="Audit Log" subtitle="Every change to marks, fees, students, notices, results — recorded." />

      <Card sx={{ p: 2, mb: 2 }}>
        <TextField placeholder="Filter by actor, action, entity, metadata…" value={filter} onChange={(e) => setFilter(e.target.value)} />
      </Card>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Time</TableCell>
              <TableCell>Actor</TableCell>
              <TableCell>Action</TableCell>
              <TableCell>Entity</TableCell>
              <TableCell>Metadata</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.map((e) => (
              <TableRow key={e.id} hover>
                <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{formatDateTime(e.createdAt)}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{e.actorName}</TableCell>
                <TableCell><Chip size="small" color={(ACTION_COLORS[e.action] as any) ?? 'default'} label={e.action} /></TableCell>
                <TableCell><Typography variant="caption" sx={{ fontFamily: 'monospace' }}>{e.entityType}#{String(e.entityId).slice(0, 12)}</Typography></TableCell>
                <TableCell sx={{ fontFamily: 'monospace', fontSize: 11, color: 'text.secondary' }}>
                  {Object.keys(e.metadata).length ? JSON.stringify(e.metadata) : '—'}
                </TableCell>
              </TableRow>
            ))}
            {filtered.length === 0 && <TableRow><TableCell colSpan={5} sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>No matching entries.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
